import { useRef } from 'react';
import {
    AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader,
    AlertDialogOverlay, Button, Text, VStack,
} from '@chakra-ui/react';

// 主動開啟對話前的班級選擇（桌面 TeacherChatPage 與老師 PWA MobileChatList 共用）。
//
// 【為什麼行政/管理員一定要選，不能讓系統挑】
// 班級不是標籤，是**權限與通知的路由依據**：WS 即時推送走 broadcast_to_class(class_id)、
// 離線推播收件人走該班的帶班老師（後端 chat.py `_teacher_ids_of_class`）。不指定 class_id 時
// 後端 `_pick_conversation_class_id` 會自動挑一個班 —— 等於系統替管理員決定「把哪一個班的
// 老師拉進這條對話、由誰開始收到家長的訊息」，而管理員完全不會知道發生了這件事。
// 帶班老師沒有這個問題：他的候選班已經被後端收斂成自己帶的班，挑到哪一個都是自己的班，
// 所以維持原本的自動挑（user 2026-08-19 拍板的範圍就是管理員）。
//
// 【為什麼只有跨多班時才問】只有一個候選班時沒有東西可選，多一次點擊只是干擾——呼叫端會
// 直接帶那個班的 id 過去（明講而不是靠自動挑，語意上兩者同義但不依賴後端的挑選規則）。
//
// 只負責「問出一個 class_id」，不打 API：開對話的錯誤處理（toast、連點防呆）已經在兩個
// 呼叫端各自的 openWithStudent 裡，搬進來只會變成同一件事維護兩份。
// student ＝ null 時整個框是關閉狀態（Chakra 的 isOpen 控制），呼叫端一個 state 就夠。
export default function ClassPickerDialog({ student, onClose, onPick }) {
    const cancelRef = useRef(null);
    const classes = student?.classes || [];

    return (
        <AlertDialog
            isOpen={!!student}
            leastDestructiveRef={cancelRef}
            onClose={onClose}
            isCentered
        >
            <AlertDialogOverlay>
                {/* AlertDialog 自帶白底 → 內文用 neutral 階（onCanvas 階是為畫布設計的，不適用於此） */}
                <AlertDialogContent mx={4}>
                    <AlertDialogHeader fontSize="md" fontWeight="bold" color="neutral.800">
                        要在哪一個班開啟對話？
                    </AlertDialogHeader>
                    <AlertDialogBody>
                        <Text fontSize="sm" color="neutral.600">
                            {student?.name || '這位學生'} 同時在多個班級。
                        </Text>
                        {/* 對話已改成每位家長各一條，所以這個框問的是「這位**家長**的對話要開在哪一班」。
                            不寫出家長是誰的話，管理員在兩列長得一樣的名冊上點了其中一列，
                            到這個框已經分不出自己點的是爸爸還是媽媽。家長身分來自名冊、不需要選。 */}
                        {student?.parentName && (
                            <Text fontSize="sm" color="neutral.700" fontWeight="medium" mt={1}>
                                對話對象：{student.parentName}
                            </Text>
                        )}
                        {/* 講清楚選項的後果：這是「選班級」唯一真正的差別，不寫出來使用者只會覺得
                            系統在無謂地多問一次 */}
                        <Text fontSize="xs" color="neutral.500" mt={2}>
                            對話會掛在你選的班級底下，家長的訊息也會通知該班的帶班老師。
                        </Text>
                        <VStack spacing={2} align="stretch" mt={4}>
                            {classes.map((c) => (
                                <Button
                                    key={c.id}
                                    size="sm"
                                    variant="outline"
                                    colorScheme="brand"
                                    justifyContent="flex-start"
                                    onClick={() => onPick?.(c.id)}
                                >
                                    {c.name}
                                </Button>
                            ))}
                        </VStack>
                    </AlertDialogBody>
                    <AlertDialogFooter>
                        <Button ref={cancelRef} size="sm" onClick={onClose}>
                            取消
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialogOverlay>
        </AlertDialog>
    );
}
